import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import * as components from '@qpr/components';
import React, { useEffect, useState } from 'react';
import ReactGridLayout, { Responsive, WidthProvider } from 'react-grid-layout';
import { Section } from './configurable-grid-state';
import ConfigurableGridItem from './ConfigurableGridItem';

interface ConfigurableGridProps {
  contents?: Section[];
  onSave?: (value: ReactGridLayout.Layouts) => void;
}

const ResponsiveGridLayout = WidthProvider(Responsive);

const ConfigurableGrid = ({ contents = [], onSave }: ConfigurableGridProps) => {
  const rowHeight: number = 1;

  const theme = useTheme();

  const originalLayouts =
    {
      lg: contents.map((item: Section, index: number) => {
        return { ...item.dimensions, i: `${index}` };
      })
    } || {};

  const [editAllowed, setEditAllowed] = useState<boolean>(false);
  const [storedLayouts, setLayouts] = useState<ReactGridLayout.Layouts>(originalLayouts);

  useEffect(() => {
    const initData = async () => {
      setLayouts(originalLayouts);
    };
    initData();
  }, []);

  const onLayoutSave = (value: ReactGridLayout.Layouts) => {
    if (onSave !== undefined) {
      onSave(value);
    }
  };

  const onItemResize = (id: string) => (height: number) => {
    const itemToCheck = storedLayouts.lg.find((item: ReactGridLayout.Layout) => item.i === id);
    const itemHasCorrectSize = itemToCheck?.h === height + parseFloat(theme.spacing(1));

    if (!itemHasCorrectSize) {
      const newArray = [...storedLayouts.lg];
      const elementsIndex = newArray.findIndex(element => element.i === id);
      newArray[elementsIndex] = {
        ...newArray[elementsIndex],
        h: height + parseFloat(theme.spacing(1))
      };
      setLayouts({ lg: newArray });
    }
  };

  const chooseComponent = (section: Section) => {
    const unsafeComponents = components as Record<string, any>;
    const Component = unsafeComponents[section.component];

    return Component !== undefined ? (
      <Component {...section.settings} />
    ) : (
      <div>
        {`invalid visualization "${section.component}" was given for element with title "${
          section.settings.title
        }". Allowed values are ${Object.keys(unsafeComponents).join(', ')}`}
      </div>
    );
  };

  const getContents = (contentsArray: Section[]) => {
    const mapped = contentsArray.map((section: Section, index: number) => (
      <div key={`${index}`}>
        <ConfigurableGridItem onResizeFunction={onItemResize(`${index}`)}>
          {chooseComponent(section)}
        </ConfigurableGridItem>
      </div>
    ));
    return mapped;
  };

  const onLayoutChange = (layouts: ReactGridLayout.Layouts) => {
    setLayouts(layouts);
  };

  const showAdminFunctionality = false;

  return (
    <>
      {showAdminFunctionality && (
        <Button size="small" aria-label="properties" onClick={() => setEditAllowed(!editAllowed)}>
          {editAllowed ? 'click to set static' : 'click to allow editing'}
        </Button>
      )}
      {showAdminFunctionality && editAllowed ? (
        <Button size="small" aria-label="properties" onClick={() => onLayoutSave(storedLayouts)}>
          Save layout
        </Button>
      ) : null}
      <ResponsiveGridLayout
        autoSize
        rowHeight={rowHeight}
        layouts={storedLayouts}
        margin={[parseFloat(theme.spacing(1)), 0]}
        breakpoints={{ sm: 600, lg: 1200 }}
        cols={{ sm: 1, lg: 12 }}
        isDraggable={editAllowed}
        isResizable={editAllowed}
        onLayoutChange={(layout, layouts) => onLayoutChange(layouts)}
      >
        {getContents(contents)}
      </ResponsiveGridLayout>
    </>
  );
};

export default ConfigurableGrid;
